//
// Copyright (c) 2018 Nutanix Inc. All rights reserved.
//
// LoginPage - Contains the components that make up the "Login" page. This will
// redirect users to their dashboard depending on if the login is successful.
//
// Libs
import PropTypes from 'prop-types';
import { get } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';

// Components
import {
    Alert,
    FormItemInput,
    Title,
    Loader,
    StackingLayout,
    Link,
    Divider,
    Paragraph,
    FlexLayout,
    NotificationTrigger,
    NotificationItem,
} from '@nutanix-ui/prism-reactjs';
import {
    apiFetchRequest,
    getWelcomeBannerAction,
    hideWelcomeBannerAction,
    getPrismVersionAction,
    getV4APIsAction,
    loginRequestUpdate,
} from '../../actions';
import {
    getErrorMsg,
    shouldShowErrorMessage,
    setWelcomeBannerHasTriggered,
    getWelcomeBannerHasTriggered,
    getVersionString,
    getLoginCustomObj,
    forceRedirect,
    authRequestExist,
    isUserLoggedIn,
    removeSessionValue,
    getSessionId,
    setToLocal,
    getServiceProviderEnabled,
    handleV4Error,
} from '../../utils';
import { isNcOnPremDeployment } from 'shared-utils/deploymentUtil';
import { LoginLayout } from '../../components/Common';
import {
    LDAP_TYPE,
    LOCAL_TYPE,
    SAML_TYPE,
    STORAGE_USERNAME,
    REFRESH_ERR_CODE,
    NC_DEFAULT_VERSION,
    GET_PROVIDERS_LIST,
    GET_LOGIN_REQUEST,
} from '../../constants';
import { PATH_URL, HOME_URL, HANDLE_ERROR } from '../../constant';

import WelcomeBanner from '../../components/WelcomeBanner';
import UserGuideLink from '../UserGuideLink';
import iamApis from '../../api/iam-apis';
import './LoginPage.less';

// TO-DO: REMOVE LATER TO USE THE ntnx-I18n library
import getI18nService from '../../i18n/I18n';
import AlertMessage from 'components/alert-message';
import { createNoRetryLoginRequestsApi } from '../../utils/apiClientOverrides';

// Create LoginRequestsApi instance with retries disabled only for this API
let loginRequestsApi = createNoRetryLoginRequestsApi();

const i18n = getI18nService();

export class LoginPage extends React.Component {
    static propTypes = {
        isAPIsPending: PropTypes.bool,
        history: PropTypes.object,
        connectorsList: PropTypes.array,
        customLoginArray: PropTypes.array,
        isAuthenticated: PropTypes.bool,
        location: PropTypes.object,
        apiFetchRequest: PropTypes.func,
        systemConfig: PropTypes.object,
        initialLoginRequest: PropTypes.object,
        softwareVersion: PropTypes.string,
        errorMessage: PropTypes.string,
        shouldPresentAsGeneralError: PropTypes.bool,
        hideWelcomeBannerAction: PropTypes.func,
        getWelcomeBannerAction: PropTypes.func,
        getPrismVersionAction: PropTypes.func,
        loginRequestUpdate: PropTypes.func,
        getV4APIsAction: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.state = {
            username: '',
            password: '',
            errorMessage: '',
            showError: shouldShowErrorMessage(props.location.search),
            showChangePWSuccess: props.location.state
                ? props.location.state.changePWSuccess
                : false,
            errorCode: props.location.search,
            isFetching: false,
        };
        this.isNcOnPremDeployment = isNcOnPremDeployment();
    }

    componentDidMount() {
        const iamSessionid = getSessionId();
        this.props.getV4APIsAction({
            name: GET_LOGIN_REQUEST,
            options: iamSessionid,
        });
        this.props.getWelcomeBannerAction();

        // Skip the pre_login_details api call to PC in case of NC on prem deployment
        if (!this.isNcOnPremDeployment) {
            this.props.getPrismVersionAction(iamApis.GET_PRISM_VERSION);
        }

        // Listen for user coming back
        window.addEventListener('focus', this.checkSession);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    componentWillUnmount() {
        window.removeEventListener('focus', this.checkSession);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    componentDidUpdate(prevProps) {
        // Show AlertMessage for non-general errors
        if (
            !this.props.shouldPresentAsGeneralError &&
            this.props.errorMessage?.length &&
            !this.state.showError
        ) {
            this.setState({ showError: true, errorMessage: this.props.errorMessage });
        }

        // Hide AlertMessage and show Notification for general errors
        if (
            this.props.shouldPresentAsGeneralError &&
            this.props.errorMessage?.length &&
            (prevProps.errorMessage !== this.props.errorMessage ||
                prevProps.shouldPresentAsGeneralError !== this.props.shouldPresentAsGeneralError)
        ) {
            if (this.state.showError) {
                this.setState({ showError: false, errorMessage: '' });
            }
            NotificationTrigger.add({
                message: this.props.errorMessage,
                autoDismissDelaySecs: 0,
                type: NotificationItem.NotificationIconType.ERROR,
            });
        }
    }

    handleVisibilityChange = () => {
        if (!document.hidden) {
            this.checkSession();
        }
    };

    checkSession() {
        const iamSessionid = getSessionId();
        if (!iamSessionid) {
            window.location.reload();
        }
    }

    handleOnSubmit(event) {
        event.preventDefault();
        const formData = new FormData(event.target);
        const username = formData.get('username');
        const password = formData.get('password');
        const iamSessionid = getSessionId();
        loginRequestsApi
            .validateCredentials(iamSessionid, { username, password })
            .then(apiResponse => {
                const responseText = JSON.parse(apiResponse.response.text);
                window.location.href = responseText.data.authCodeUri;
            })
            .catch(error => {
                handleV4Error(error, errorMessage => {
                    this.setState({
                        isFetching: false,
                        showError: true,
                        errorMessage,
                    });
                });
            });
    }

    render() {
        const {
            systemConfig,
            softwareVersion,
            customLoginArray,
            connectorsList = [],
            isAPIsPending,
            initialLoginRequest,
        } = this.props;

        const { password, username, showError, showChangePWSuccess, errorCode, isFetching } =
            this.state;

        const isServiceProviderEnabled = getServiceProviderEnabled(initialLoginRequest);
        const customLoginObj = getLoginCustomObj(customLoginArray, this.isNcOnPremDeployment);
        const authReqExist = authRequestExist(initialLoginRequest);
        const isUserLoggedInValue = isUserLoggedIn(connectorsList?.length, initialLoginRequest);

        const connectorType = this.getConnector();

        let titleMessage = '';
        let footerMessage = '';

        /**
         * When IAM AuthN backend session has expired, it will provider page from redirect with "ec=ERR-412"
         * or when IAM AuthN request cookie isn't exist(auth_req_exists == false),
         * Will send user back to Prism landing page.
         */

        if (errorCode.includes(REFRESH_ERR_CODE) || !authReqExist || isUserLoggedInValue) {
            forceRedirect(HOME_URL);
        }

        const errorMsg = getErrorMsg(errorCode);

        const otherConn = connectorType ? this.getOtherConnectors(connectorType.name) : null;
        const otherType = otherConn?.length === 1 ? otherConn[0].type : null;

        if (connectorsList && !isServiceProviderEnabled) {
            // if conn.length is greater than 2 or  a local and a SAML connector
            // it should show in the login screen the title 'login with <connector name>
            // and a footer with 'login with other methods' link to return to the connectors screen
            if (
                connectorsList.length > 2 ||
                (connectorsList.length === 2 && otherType === SAML_TYPE)
            ) {
                titleMessage =
                    connectorType?.name === LOCAL_TYPE
                        ? i18n.t('LoginPage.loginLocal')
                        : i18n.t('LoginPage.loginWith', { connectorName: connectorType.name });
                footerMessage = this.getFooterMsgJSX(connectorType.type, otherConn, false);
            }

            // if conn.length equals 2 and none of them are SAML connector
            // it should show login screen with title 'login with local' or 'login with company Id'
            // depending if the connector used is ldap or local connector
            if (connectorsList.length === 2 && otherType !== SAML_TYPE) {
                titleMessage =
                    connectorType.type === LDAP_TYPE
                        ? i18n.t('LoginPage.loginWithCompanyId')
                        : i18n.t('LoginPage.loginLocal');

                footerMessage = this.getFooterMsgJSX(connectorType.type, otherConn, false);
            }
        } else if (isServiceProviderEnabled && connectorType.type) {
            titleMessage = i18n.t('LoginPage.loggingInto', { connectorName: connectorType.name });
            footerMessage = this.getFooterMsgJSX(
                connectorType.type,
                otherConn,
                isServiceProviderEnabled
            );
        }

        const successMsg = i18n.t('LoginLayout.changePWSuccessMessage');

        const version = getVersionString(softwareVersion);
        const errorMessage = this.state.errorMessage || this.props.errorMessage || errorMsg;
        const LoginForm = (
            <form
                className="loginpage-form"
                id="login-form"
                onSubmit={this.handleOnSubmit.bind(this)}
            >
                <StackingLayout>
                    {showError && errorMessage?.length > 0 && (
                        <AlertMessage
                            message={errorMessage}
                            type={Alert.TYPE.ERROR}
                            showCloseIcon={false}
                        />
                    )}
                    {showChangePWSuccess && (
                        <AlertMessage
                            message={successMsg}
                            type={Alert.TYPE.SUCCESS}
                            showCloseIcon={false}
                        />
                    )}

                    {titleMessage !== '' ? (
                        <Title
                            className="login-form-title iam-center-text login-with-text-overflow"
                            id="login-title"
                            size="h4"
                        >
                            {titleMessage}
                        </Title>
                    ) : null}

                    <StackingLayout className="loginpage-form-content" itemGap="L">
                        <FormItemInput
                            id="inputUsername"
                            data-test="loginInputUsername"
                            value={username}
                            label={i18n.t('LoginPage.username')}
                            name="username"
                            onChange={this.handleInputChange}
                        />
                        <FormItemInput
                            id="inputPassword"
                            data-test="loginInputPassword"
                            type="password"
                            label={i18n.t('LoginPage.password')}
                            name="password"
                            onChange={this.handleInputChange}
                        />
                        <button
                            data-type="primary"
                            id="btnLogin"
                            data-test="loginButtonSubmit"
                            data-width="full-width"
                            data-appearance="default"
                            className="ant-btn ntnx ntnx-button"
                            disabled={this.disableSubmit(username, password)}
                            type="submit"
                            onClick={this.handleBtnClick}
                        >
                            {isFetching ? i18n.t('LoginPage.loading') : i18n.t('LoginPage.logIn')}
                        </button>
                        {(connectorsList.length > 1 ||
                            (connectorType && connectorType.type && isServiceProviderEnabled)) && (
                            <StackingLayout itemGap="L">
                                <FlexLayout alignItems="center">
                                    <Divider />
                                    <Paragraph className="no-wrap" type="secondary">
                                        {i18n.t('Vocabulary.or')}
                                    </Paragraph>
                                    <Divider />
                                </FlexLayout>
                            </StackingLayout>
                        )}
                        {isFetching ? null : (
                            <StackingLayout className="iam-center-text" itemGap="L">
                                {footerMessage}
                            </StackingLayout>
                        )}
                        <UserGuideLink version={version} />
                        {getWelcomeBannerHasTriggered() ? null : this.getBanner(systemConfig)}
                    </StackingLayout>
                </StackingLayout>
            </form>
        );

        return !isAPIsPending ? (
            <LoginLayout
                id="n-ctr-login"
                form={LoginForm}
                customLoginEle={customLoginObj}
                leftTitle={i18n.t('LoginLayout.reimagineText')}
            />
        ) : (
            <Loader tip="Loading..." overlay={true} />
        );
    }

    /**
     * Closes welcome banner and launches request for connectors
     * @returns {void}
     */
    handleCloseWelcomeBanner = () => {
        const { hideWelcomeBannerAction: hideWelcomeBanner } = this.props;
        hideWelcomeBanner('GET_WELCOME_BANNER', false);
        // Set welcome_banner_has_trigger as true in sessionStorage
        setWelcomeBannerHasTriggered(true);
    };

    /**
     * Get Welcome banner component from systemConfig
     * @param {String} systemConfig - system configure object
     * @returns {HTML} - return welcome banner to display
     */
    getBanner(systemConfig) {
        const bannerStatus = get(systemConfig, 'isEnabled') && !getWelcomeBannerHasTriggered();
        const bannerHTMLContent = get(systemConfig, 'content');

        return (
            <WelcomeBanner
                {...{ bannerHTMLContent, bannerStatus }}
                onHandleCloseBanner={this.handleCloseWelcomeBanner}
            />
        );
    }

    /**
     * Get footer message JSX based on connector type, get connectorUrl by filter current connector
     * @param {String} type - connector type string.
     * @param {Array} otherConn - array of other connectors.
     * @param {Boolean} isServiceProviderEnabled - true/false
     * @returns {Element} - Footer Message Element
     **/
    getFooterMsgJSX = (type, otherConn, isServiceProviderEnabled) => {
        if (isServiceProviderEnabled) {
            return (
                <Link
                    id="loginWithOther"
                    data-test="loginLinkWithOther"
                    onClick={this.handleGotoProviders}
                >
                    {i18n.t('LoginPage.otherMethods')}
                </Link>
            );
        }
        // Handle error when no type or no other connectors object in array
        if (!type || !otherConn || !otherConn.length) {
            console.error('ERR: missing connector type or no other Connector', type, otherConn);
            return <div />;
        }

        // Return Footer JSX for one other connectors as SAML Type
        // or more than 2 other connectors
        if (otherConn.length > 1 || (otherConn.length === 1 && otherConn[0].type === SAML_TYPE)) {
            return (
                <Link
                    id="loginWithOther"
                    data-test="loginLinkWithOther"
                    onClick={this.handleGotoProviders}
                >
                    {i18n.t('LoginPage.otherMethods')}
                </Link>
            );
        }

        // When otherConn only have one connector and it is local connector or LDAP connector
        // Return Footer JSX based on type
        return type === LOCAL_TYPE ? (
            <Link
                id="loginWithAd"
                data-test="loginLinkWithAd"
                onClick={this.handleLinkRedirect(otherConn[0])}
            >
                {i18n.t('LoginPage.loginWithCompanyId')}
            </Link>
        ) : (
            <Link
                id="loginWithLocal"
                data-test="loginLinkWithLocal"
                onClick={this.handleLinkRedirect(otherConn[0])}
            >
                {i18n.t('LoginPage.loginLocal')}
            </Link>
        );
    };

    /**
     * Handles redirection to providers screen.
     * @returns {void}
     */
    handleGotoProviders = () => {
        const { history } = this.props;
        history.push(PATH_URL.PROVIDERS); // '/providers'
    };

    /**
     * Gets connector object from local storage
     * @returns {Object} - connector object
     **/
    getConnector = () => {
        return JSON.parse(localStorage.getItem('connector')) || {};
    };

    /**
     * Save user input username to session storage, and will be used by changePassword page
     * @param {String} usernameString - string of username.
     * @returns {void}
     **/
    saveUsernameToSession = usernameString => {
        sessionStorage.setItem(STORAGE_USERNAME, usernameString);
    };

    /**
     * Disable submit button if either username or password is empty
     * @param {String} username - string of username.
     * @param {String} password - string of password.
     * @returns {boolean} should disable submit button
     **/
    disableSubmit = (username, password) => {
        return this.isEmptyString(username) || this.isEmptyString(password);
    };

    /**
     * Check if the string don't exist
     * @param {String} string - string.
     * @returns {boolean} is empty string
     **/
    isEmptyString = string => {
        // Not exist or no content
        return !string || string.trim().length === 0;
    };

    /**
     * Filter by current connector name to get all other connectors
     * @param {String} curConName - current connector name, get url by filter it.
     * @returns {Array} Array of other connectors object;
     **/
    getOtherConnectors = curConName => {
        return this.props.connectorsList?.filter?.(connector => connector.name !== curConName);
    };

    /**
     * Handle redirect event for bottom link click
     * @param {Object} otherConnector - other connector's object
     * @returns {void}
     **/
    handleLinkRedirect = otherConnector => () => {
        if (otherConnector) {
            const { extId } = otherConnector;
            setToLocal(otherConnector);
            removeSessionValue(HANDLE_ERROR);
            this.props.loginRequestUpdate({ loginProviderId: extId });
        } else {
            console.error('Do not get redirect URL from otherConnector: ', otherConnector);
        }
    };

    /**
     * Handles inputs change to set values in state
     * @param {Object} evt - Input event.
     * @returns {void}
     */
    handleInputChange = evt => {
        const { name, value } = evt.target;
        if (name === 'username') {
            this.saveUsernameToSession(value);
            this.setState({
                [name]: value,
                showError: false,
                errorMessage: '',
            });
        } else {
            this.setState({ [name]: value });
        }
    };

    /**
     * Handle login button click and change the loader status
     * @returns {void}
     */
    handleBtnClick = () => {
        this.setState({
            isFetching: true,
        });
        // Init error flag to sessionStorage
        removeSessionValue(HANDLE_ERROR);
    };
}

const mapStateToProps = state => {
    return {
        isAPIsPending: state.api.pending,
        systemConfig: state.api.GET_WELCOME_BANNER,
        connectorsList: state.api[GET_PROVIDERS_LIST] || [],
        softwareVersion: isNcOnPremDeployment() ? NC_DEFAULT_VERSION : state.api.PRISM_VERSION,
        customLoginArray: state.api.CUSTOM_LOGIN,
        initialLoginRequest: state.api[GET_LOGIN_REQUEST],
        errorMessage: state.api.errorMessage,
        shouldPresentAsGeneralError: state.api.shouldPresentAsGeneralError,
    };
};

export default connect(mapStateToProps, {
    apiFetchRequest,
    hideWelcomeBannerAction,
    getWelcomeBannerAction,
    getPrismVersionAction,
    loginRequestUpdate,
    getV4APIsAction,
})(LoginPage);
